// Colocated Driver.js tour for PartnerDetails, targeted at super admins on
// their first sign in. Two variants share one tour id, so a super admin
// only sees the tour once and gets the one that fits their partner shape.
//
// Solo partner (exactly one user): a single orientation step introduces
// the page, notes that a starter tag and default team are already in
// place, and points at Add User as the primary next action. Teams are
// deliberately not surfaced because the Teams section is hidden while a
// partner has one user; it appears the moment a second user joins.
//
// Multi-user partner: the familiar 3-step walkthrough (expense tags,
// teams, users) with an optional closer for partners still missing any
// of the three.
//
// Both variants hand off to the shared FAQ outro (menu toggle, Help and
// FAQ, security narrative, PIN setup for anyone still without one).

import { buildFaqOutroSteps } from '../../hooks/faqOutroSteps';

export const ADMIN_DETAILS_SUPER_ADMIN_SETUP_TOUR_ID = 'admin-details-super-admin-setup-v9';

// Each CTA button is rendered twice (one for desktop, one for the mobile
// round + variant), so the same data-tour attribute matches two nodes and
// only one is visible at a time. Returning the visible one keeps the tour
// spotlight tight on the actual clickable button, so nothing else on the
// page can be triggered mid-step.
const visibleElementFor = (selector) => () => {
    const candidates = document.querySelectorAll(selector);
    for (const candidate of candidates) {
        if (candidate.offsetParent !== null) return candidate;
    }
    return null;
};

export const buildAdminDetailsSuperAdminSetupTourSteps = ({
    navigate,
    hasTransactionPin,
    completedTourIds = [],
    partnerNeedsSetup = true,
    hasSoloUser = false,
}) => {
    if (hasSoloUser) {
        return [
            {
                element: visibleElementFor('[data-tour="users-add-button"]'),
                title: 'Welcome to your Organization page',
                description: 'Your expense tags, users, and vault live here. We have set you up with a starter tag and a default team already, and a row of one-click suggested tags sits above the tag table for the common categories like Rent and Transport. Invite your first person from Add User when you are ready; once someone with a wallet joins you, we will bring in teams so approvals can route between you.',
                side: 'bottom',
                align: 'start',
            },
            ...buildFaqOutroSteps({ navigate, hasTransactionPin, completedTourIds }),
        ];
    }

    const closerStep = partnerNeedsSetup
        ? {
            element: visibleElementFor('[data-tour="admin-details-add-expense-tag-button"]'),
            title: 'Ready when you are',
            description: 'Start by adding your first expense tag, then a team, then a user, in that order. Everything else on the page opens up once each step is in place. You can do it now or come back here later from the sidebar.',
            side: 'bottom',
            align: 'start',
        }
        : null;

    return [
        {
            element: visibleElementFor('[data-tour="admin-details-add-expense-tag-button"]'),
            title: 'Step 1: Your expense tags live here',
            description: 'Expense tags are the categories you will use for spending, for example Fuel, Airtime, or Rent. Every payment your team makes gets tagged with one of these so you can see later where the money went. Come back to this button any time to add a category.',
            side: 'bottom',
            align: 'start',
        },
        {
            element: visibleElementFor('[data-tour="admin-details-suggested-expense-tags"]'),
            title: 'Or start faster with a suggestion',
            description: 'These chips add a tag with one click. Pick the ones that fit your organisation. A chip disappears the moment its tag exists, and the whole row hides once you have covered the common categories.',
            side: 'bottom',
            align: 'start',
        },
        {
            element: visibleElementFor('[data-tour="admin-details-add-team-button"]'),
            title: 'Step 2: Your teams live here',
            description: 'A team is one or more people with a shared reviewer. It can be just one person (you, or a specific budget holder) or a whole department. Every wallet holder must belong to a team, because that is how a request to raise a spending limit, or a wallet top-up that needs approval, reaches the right reviewer.',
            side: 'bottom',
            align: 'start',
        },
        {
            element: visibleElementFor('[data-tour="users-add-button"]'),
            title: 'Step 3: Your users live here',
            description: 'Now you can bring people in. Not everyone needs a wallet (an accountant, for example, can set up spending for others without one). Those who do get a wallet make tagged payments from it, and their team reviewer approves any request to raise their spending limit or top up their wallet.',
            side: 'bottom',
            align: 'start',
        },
        ...(closerStep ? [closerStep] : []),
        ...buildFaqOutroSteps({ navigate, hasTransactionPin, completedTourIds }),
    ];
};
